import { Schema } from "effect";
import { canonicalWorkroomLabel, WorldRoomEntry } from "@cleo/contracts";

import { postLocalJson } from "./localJsonTransport";
import type { WorldEntryChangeOutcome } from "./worldSessionClient";

/**
 * A room's or session's own label through the local Desk's World door
 * (2026-09-10). Claimed only when the World's read of that room or session
 * carries the word; a room names one of its root sessions (rootSessionIdOf),
 * read back with it. `roomLabel` names its room only while that room is still
 * named New room.
 */
export async function setWorldRecordLabel(
  input: {
    readonly worldKey: string;
    readonly recordId: string;
    readonly label: string;
    readonly roomLabel?: string;
    /** A room's (R_) root session; absent for a session (S_). */
    readonly sessionId?: string;
  },
  fetchImpl: typeof fetch = fetch,
): Promise<WorldEntryChangeOutcome> {
  try {
    const response = await postLocalJson("/api/neucleos/world/records/label", input, fetchImpl);
    if (response.status === 404)
      return {
        ok: false,
        kind: "unavailable",
        reason: "Renaming is not available on the running server yet. Update the server and retry.",
      };
    const body = await response.json();
    if (!response.ok || body?.ok !== true)
      return {
        ok: false,
        kind: typeof body?.kind === "string" ? body.kind : "transport",
        reason: typeof body?.reason === "string" ? body.reason : "The rename was not confirmed.",
      };
    const entry = Schema.decodeUnknownSync(WorldRoomEntry)(body.entry);
    const labelled =
      input.sessionId === undefined
        ? entry.session.sessionId === input.recordId && entry.session.label === input.label
        : entry.room.roomId === input.recordId &&
          entry.session.sessionId === input.sessionId &&
          canonicalWorkroomLabel(entry.room.workroomKey, entry.room.label) ===
            canonicalWorkroomLabel(entry.room.workroomKey, input.label);
    if (entry.worldKey !== input.worldKey || !labelled)
      return { ok: false, kind: "transport", reason: "The World did not confirm the new name." };
    return { ok: true, entry };
  } catch (cause) {
    return {
      ok: false,
      kind: "transport",
      reason: cause instanceof Error ? cause.message : "The local Cleo server is unreachable.",
    };
  }
}
